import { components } from "@certn/api/generated-schemas/client-portal";
import { DataTable, NeutralTag, SuccessTag } from "@certn/cascadia-react";
import { SortDescriptor } from "@react-types/shared";
import { t } from "i18next";
import { ResizableTableContainer } from "react-aria-components";
import { useNavigate } from "react-router-dom";

import { BillingProfilesActionDropdownMenu } from "./components/BillingProfilesActionsDropdownMenu";

interface GroupProps {
  id: string;
  name: string;
}

export function BillingProfilesTable(props: {
  onSortChange: (sortDescriptor: SortDescriptor) => void;
  sortDescriptor?: SortDescriptor;
  results: components["schemas"]["BillingProfileList"][] | undefined;
  isLoading: boolean;
  showPayerColumn?: boolean;
}) {
  const { onSortChange, sortDescriptor, results, isLoading, showPayerColumn } =
    props;
  const navigate = useNavigate();

  const getGroupNames = (
    profile: components["schemas"]["BillingProfileList"]
  ) => {
    if (!profile.customergroup_set) {
      return "--";
    }

    return profile.customergroup_set
      .map((group: GroupProps) => group.name)
      .join(", ");
  };

  return (
    <ResizableTableContainer>
      <DataTable
        columns={[
          {
            name: t("Profile name"),
            key: "profile_name",
            isRowHeader: true,
            allowsSorting: true,
            testID: "profile-name-header",
            render: (billingProfile) => billingProfile?.profile_name ?? "--",
          },
          {
            name: t("Client name"),
            key: "customer_name",
            allowsSorting: true,
            testID: "customer-name-header",
            render: (billingProfile) => billingProfile?.customer_name ?? "--",
          },
          ...(showPayerColumn
            ? [
                {
                  name: t("Payer"),
                  key: "payer_option",
                  testID: "payer-header",
                  render: (
                    billingProfile: components["schemas"]["BillingProfileList"]
                  ) => {
                    const payer = billingProfile?.payer_option;
                    if (payer === "APPLICANT") return t("Applicant");
                    if (payer === "CLIENT") return t("Client");
                    return "--";
                  },
                },
              ]
            : []),
          {
            name: t("Currency"),
            key: "currency",
            allowsSorting: true,
            testID: "currency-header",
            render: (billingProfile) => billingProfile?.currency ?? "--",
          },
          {
            name: t("Group(s)"),
            key: "group",
            testID: "group-header",
            render: (billingProfile) => getGroupNames(billingProfile),
          },
          {
            name: t("Status"),
            key: "status",
            testID: "status-header",
            render: (billingProfile) =>
              billingProfile?.archived ? (
                <NeutralTag type="subtle">{t("Archived")}</NeutralTag>
              ) : (
                <SuccessTag type="subtle">{t("Active")}</SuccessTag>
              ),
          },
          {
            key: "actions",
            isAction: true,
            width: "10%",
            render(billingProfile) {
              return (
                <BillingProfilesActionDropdownMenu
                  profileId={billingProfile?.id || ""}
                  archived={billingProfile?.archived || false}
                />
              );
            },
          },
        ]}
        rows={results || []}
        isLoading={isLoading}
        renderEmptyState={() => <div>{t("No billing profiles")}</div>}
        aria-label="customer-billing-profile-table"
        onSortChange={onSortChange}
        sortDescriptor={sortDescriptor}
        onRowAction={(id) => {
          navigate(`${id}`);
        }}
      />
    </ResizableTableContainer>
  );
}
